/**
 * What each role may do. The single source of truth for permissions: the server's admin()
 * plugin enforces it, the client's adminClient() and the Staff page read it.
 *
 * - A *statement* lists every resource and the actions that exist on it.
 * - A *role* grants a subset of those actions. Users get roles on the Staff page; Better Auth
 *   stores them comma-separated in `user.role` ("user,standards:edit"), and a user may do anything
 *   any of their roles allows.
 *
 * Roles come in two kinds:
 * - `admin` is the global admin: it manages users (Staff page, Amp projects), may assign any role
 *   and has every action on every service.
 * - Each service has `<service>:read`, `<service>:edit` and `<service>:admin`; each level includes
 *   the ones below it. `<service>:admin` may also list and open staff on the Staff page, and assign
 *   that service's roles to anyone (src/server/service-roles.ts), but no other role.
 *
 * To add a service: add it to `services` and `statement`, and any action it checks besides read,
 * edit and admin to `editActions`. Role names are validated on the server, so a role must exist
 * here before it can be assigned.
 */
import { createAccessControl } from 'better-auth/plugins/access'
import { adminAc, defaultStatements } from 'better-auth/plugins/admin/access'

/** Every service whose access is managed here: authoring.edclub.com, standards.edclub.tech, registry.edclub.tech. */
export const services = ['authoring', 'standards', 'registry'] as const
export type Service = (typeof services)[number]

export const levels = ['read', 'edit', 'admin'] as const
export type Level = (typeof levels)[number]

export const statement = {
	// Better Auth admin plugin: user management on the Staff page (/api/auth/admin/*).
	...defaultStatements,
	authoring: ['read', 'edit', 'admin'],
	// standards.edclub.tech checks `edit` to start "Fetch all", "Align all" and uploads.
	standards: ['read', 'edit', 'admin'],
	// registry.edclub.tech (not in this repo) still checks `deploy` to deploy an experience (edclub
	// CLI); drop it once the registry checks `edit`. Amp projects are usually approved with just `registry:edit`.
	registry: ['read', 'edit', 'deploy', 'admin']
} as const

/** Legacy actions a service still checks, granted from `edit` up. */
const editActions: Partial<Record<Service, string[]>> = { registry: ['deploy'] }

export const ac = createAccessControl(statement)

/** What `<service>:<level>` grants on its service: its level and every level below it. */
const grants = (service: Service, level: Level) => {
	const actions: string[] = levels.slice(0, levels.indexOf(level) + 1)
	return level === 'read' ? actions : [...actions, ...(editActions[service] ?? [])]
}

export type ServiceRole = `${Service}:${Level}`
export const serviceRole = (service: Service, level: Level): ServiceRole => `${service}:${level}`

const serviceRoles = Object.fromEntries(
	services.flatMap((service) =>
		levels.map((level) => [
			serviceRole(service, level),
			// A service admin finds staff on the Staff page to give them this service's roles.
			ac.newRole({ [service]: grants(service, level), ...(level === 'admin' ? { user: ['list', 'get'] } : {}) })
		])
	)
) as Record<ServiceRole, ReturnType<typeof ac.newRole>>

export const roles = {
	user: ac.newRole({}),
	admin: ac.newRole({ ...adminAc.statements, ...Object.fromEntries(services.map((service) => [service, statement[service]])) }),
	...serviceRoles
}

export type RoleName = keyof typeof roles
export const roleNames = Object.keys(roles) as RoleName[]

type Statement = typeof statement
/** Resolved permissions: `{ authoring: ['read', 'edit'] }`. Resources with no granted actions are left out. */
export type Permissions = { [R in keyof Statement]?: Statement[R][number][] }

/** Parses Better Auth's comma-separated `user.role`, dropping roles that aren't defined here. */
export const parseRoles = (role: string | null | undefined): RoleName[] =>
	(role ?? '')
		.split(',')
		.map((r) => r.trim())
		.filter((r): r is RoleName => Object.hasOwn(roles, r))

/** `roles` with `service`'s role replaced by `<service>:<level>`, or removed if `level` is null. */
export const withServiceLevel = (roles: readonly string[], service: Service, level: Level | null) => [
	...roles.filter((role) => !role.startsWith(`${service}:`)),
	...(level ? [serviceRole(service, level)] : [])
]

/** The `<service>:<level>` role in `role`, or null. The highest level wins if there are several. */
export const serviceLevel = (role: string | null | undefined, service: Service): Level | null =>
	levels.findLast((level) => parseRoles(role).includes(serviceRole(service, level))) ?? null

/**
 * Everything a user with this `user.role` may do: the union of their roles' grants. This is
 * the shape to put on the session cookie, so other apps can check permissions without
 * knowing the role definitions.
 */
export function permissionsFor(role: string | null | undefined): Permissions {
	const granted = new Map<string, Set<string>>()
	for (const name of parseRoles(role))
		for (const [resource, actions] of Object.entries(roles[name].statements as Record<string, readonly string[]>))
			for (const action of actions) granted.set(resource, (granted.get(resource) ?? new Set()).add(action))
	return Object.fromEntries([...granted].map(([resource, actions]) => [resource, [...actions]]))
}

/** Services whose roles this `user.role` may assign: all for a global admin, else those it is `<service>:admin` of. */
export const administeredServices = (role: string | null | undefined): Service[] => {
	const permissions = permissionsFor(role)
	return services.filter((service) => (permissions[service] as string[] | undefined)?.includes('admin'))
}
